import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import { useTranslation } from 'react-i18next'
import { TableBody } from '@material-ui/core'
import {
  Button,
  Checkbox,
  FormModal,
  FormModalActions,
  FormModalContent,
  FormModalTitle,
  Loader,
  Table,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from 'sputnik-ui'

import { fetchDeviceKeys } from './installationsSlice'

// Экспорт веб-интерфейса контроллера: `HEX;"комментарий";door_access`, без заголовка.
// Здесь разбираем его только ради счётчика — импортирует и валидирует бэкенд.
const countKeysInCsv = (text) =>
  text
    .split('\n')
    .map((line) => line.split(';')[0].trim().replace(/"/g, ''))
    .filter((hex) => /^[0-9a-fA-F]+$/.test(hex) && hex.length % 2 === 0).length

// Шаг между выбором контроллера Eltex и установкой на подъезд (business_apps#1468).
// Показывает ключи, залитые в контроллер напрямую, и добавляет их на подъезд:
// иначе синхронизация ключей после привязки сотрёт их с устройства.
export default function EltexKeysModal({
  open,
  uuid,
  pending,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation(['common', 'devices'])
  const dispatch = useDispatch()
  const fileRef = useRef()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [keys, setKeys] = useState([])
  const [total, setTotal] = useState(0)
  const [truncated, setTruncated] = useState(false)
  const [file, setFile] = useState(null)
  const [fileKeysCount, setFileKeysCount] = useState(0)

  useEffect(() => {
    if (!open || !uuid) return undefined

    // Ответ предыдущего чтения (повторное открытие, другое устройство) игнорируем:
    // иначе его ошибка ложится поверх нового списка и навсегда гасит «Подтвердить».
    let cancelled = false

    setLoading(true)
    setError(null)
    setKeys([])
    setTotal(0)
    setTruncated(false)
    setFile(null)
    setFileKeysCount(0)

    dispatch(fetchDeviceKeys({ uuid }))
      .then(unwrapResult)
      .then((payload) => {
        if (cancelled) return

        setKeys(payload.keys || [])
        setTotal(payload.total || 0)
        setTruncated(Boolean(payload.truncated))
      })
      .catch((err) => {
        if (cancelled) return

        // В err приходит либо текст ошибки бэкенда, либо объект ошибки запроса —
        // объект отрендерить как текст нельзя, он роняет дерево целиком.
        setError(
          typeof err === 'string' && err
            ? err
            : t('devices:eltex_keys.read_error')
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [dispatch, open, uuid, t])

  const handleChooseFile = () => fileRef.current.click()

  const handleFile = useCallback((event) => {
    const chosen = event.target.files[0]
    if (!chosen) return

    setFile(chosen)

    const reader = new FileReader()
    reader.onload = () => setFileKeysCount(countKeysInCsv(reader.result))
    reader.readAsText(chosen)
  }, [])

  const handleClose = () => {
    if (fileRef.current) fileRef.current.value = null
    onClose()
  }

  // Ключи с неполного списка добавлять нельзя — надмножество из него не построить,
  // поэтому при обрезке подтверждение доступно только с файлом из веб-интерфейса.
  const confirmDisabled =
    loading || pending || Boolean(error) || (truncated && !file)

  return (
    <FormModal open={open} onClose={handleClose}>
      <FormModalTitle>{t('devices:eltex_keys.title')}</FormModalTitle>
      <FormModalContent>
        {loading && <Loader />}

        {!loading && error && <Typography color="error">{error}</Typography>}

        {!loading && !error && truncated && (
          <>
            <Typography color="error" style={{ marginBottom: '1.5rem' }}>
              {t('devices:eltex_keys.truncated')}
            </Typography>
            <Typography color="textSecondary" style={{ marginBottom: '1rem' }}>
              {t('devices:eltex_keys.truncated_hint')}
            </Typography>
            <Button secondary variant="v2" onClick={handleChooseFile}>
              {t('devices:eltex_keys.choose_file')}
            </Button>
            {file && (
              <Typography style={{ marginTop: '1rem' }}>
                {t('devices:eltex_keys.file_chosen', {
                  name: file.name,
                  count: fileKeysCount,
                })}
              </Typography>
            )}
          </>
        )}

        {!loading && !error && !truncated && (
          <>
            <Typography color="textSecondary" style={{ marginBottom: '1rem' }}>
              {t('devices:eltex_keys.found', { count: total })}
            </Typography>
            {/* Ключей в контроллере пилота — сотни, список скроллим внутри модалки. */}
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('devices:eltex_keys.hex')}</TableCell>
                    <TableCell>{t('devices:eltex_keys.comment')}</TableCell>
                    <TableCell>{t('devices:eltex_keys.doors')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {keys.map((key) => (
                    <TableRow key={key.hex}>
                      <TableCell>{key.hex}</TableCell>
                      <TableCell>{key.comment || '—'}</TableCell>
                      <TableCell>
                        {key.door_access === 3
                          ? t('devices:eltex_keys.both_doors')
                          : t('devices:eltex_keys.first_door')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Снять галку нельзя: без ключей на подъезде синхронизация сотрёт их
                с контроллера. Отказаться от установки можно только кнопкой «Отмена». */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '1.5rem',
              }}
            >
              <Checkbox
                checked
                disabled
                color="blue"
                style={{ margin: '0px', padding: '0px', marginRight: 16 }}
              />
              <Typography>{t('devices:eltex_keys.add_to_entry')}</Typography>
            </div>
          </>
        )}

        <input
          type="file"
          accept=".csv"
          ref={fileRef}
          style={{ display: 'none' }}
          onChange={handleFile}
        />
      </FormModalContent>

      <FormModalActions>
        <Button
          primary
          variant="v2"
          fullWidth
          disabled={confirmDisabled}
          onClick={() => onConfirm(file)}
        >
          {pending
            ? t('devices:eltex_keys.importing')
            : t('common:button.confirm_capital')}
        </Button>
        {/* Пока идёт импорт, закрывать нельзя: запрос не отменяется, и по его
            завершении устройство всё равно привязалось бы. */}
        <Button tertiary variant="v2" disabled={pending} onClick={handleClose}>
          {t('common:button.cancel')}
        </Button>
      </FormModalActions>
    </FormModal>
  )
}
